// hooks/playground/useScenarioAPI.js - CORRECTED

import { useState, useCallback } from 'react';
import apiService from '../../../services/api';
import { ENDPOINTS } from '../../../services/endpoints';

export const useScenarioAPI = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all scenarios
   */
  const fetchScenarios = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // console.log('📡 [API] Fetching scenarios...');
      const response = await apiService.get(ENDPOINTS.PL_SCENARIOS_LIST);
      
      // console.log('✅ [API] Fetched scenarios:', response.scenarios.length);
      return response.scenarios;
    } catch (err) {
      console.error('❌ [API] Error fetching scenarios:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch single scenario by ID
   */
  const fetchScenario = useCallback(async (scenarioId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('📡 [API] Fetching scenario:', scenarioId);
      const scenario = await apiService.get(ENDPOINTS.PL_SCENARIOS_GET(scenarioId));
      
      console.log('✅ [API] Fetched scenario:', scenario.scenario_name);
      return scenario;
    } catch (err) {
      console.error('❌ [API] Error fetching scenario:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create new scenario
   */
  const createScenario = useCallback(async (scenarioData) => {
    setIsSaving(true); // ✅ ADD: Set saving state
    setError(null);
    
    try {
      console.log('📡 [API] Creating scenario:', scenarioData.scenario_name);
      console.log('📦 [API] Full payload:', JSON.stringify(scenarioData, null, 2));
      
      const response = await apiService.post(ENDPOINTS.PL_SCENARIOS_LIST, scenarioData);
      console.log('✅ [API] Scenario created:', response);
      return response;
    } catch (err) {
      console.error('❌ [API] Error creating scenario:', err);
      console.error('📦 [API] Failed payload:', scenarioData);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false); // ✅ ADD: Clear saving state
    }
  }, []);

  /**
   * Update existing scenario
   */
  const updateScenario = useCallback(async (scenarioId, updates) => {
    setIsSaving(true);
    setError(null);
    
    try {
      console.log('📡 [API] Updating scenario:', scenarioId);
      
      const scenario = await apiService.patch(
        ENDPOINTS.PL_SCENARIOS_UPDATE(scenarioId),
        updates
      );
      
      console.log('✅ [API] Updated scenario:', scenario.scenario_id);
      return scenario;
    } catch (err) {
      console.error('❌ [API] Error updating scenario:', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Delete scenario
   * ✅ FIXED: Handle 204 No Content response
   */
  const deleteScenario = useCallback(async (scenarioId) => {
    setIsSaving(true);
    setError(null);
    
    try {
      console.log('📡 [API] Deleting scenario:', scenarioId);
      
      // ✅ DELETE returns 204 No Content - don't expect JSON response
      const response = await apiService.delete(ENDPOINTS.PL_SCENARIOS_DELETE(scenarioId));
      
      // ✅ Response will be null for 204, which is success
      console.log('✅ [API] Deleted scenario:', scenarioId);
      return true;
    } catch (err) {
      console.error('❌ [API] Error deleting scenario:', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  /**
   * Duplicate scenario
   */
  const duplicateScenario = useCallback(async (scenarioId, newName) => {
    setIsSaving(true);
    setError(null);
    
    try {
      console.log('📡 [API] Duplicating scenario:', scenarioId, 'as:', newName);
      
      const scenario = await apiService.post(
        ENDPOINTS.PL_SCENARIOS_DUPLICATE(scenarioId),
        null,
        { new_name: newName }
      );
      
      console.log('✅ [API] Duplicated scenario:', scenario.scenario_id);
      return scenario;
    } catch (err) {
      console.error('❌ [API] Error duplicating scenario:', err);
      setError(err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    // State
    isLoading,
    isSaving,
    error,
    
    // Operations
    fetchScenarios,
    fetchScenario,
    createScenario,
    updateScenario,
    deleteScenario,
    duplicateScenario,
  };
};